import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function DeleteAccount() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/account");
      toast.success("Your account and all records were permanently deleted");
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ek-page)" }} data-testid="delete-account-view">
      <header className="border-b sticky top-0 z-20" style={{ background: "rgba(7,11,20,0.9)", backdropFilter: "blur(12px)", borderColor: "var(--ek-border)" }}>
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/ek-logo.png" alt="EvidenceKeeper" className="h-9 w-9 object-contain" />
            <span className="font-head font-bold tracking-tight">EvidenceKeeper</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm transition-colors hover:text-slate-200" style={{ color: "var(--ek-text-muted)" }} data-testid="delete-back-link">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="ek-stamp inline-block mb-3">DANGER ZONE</div>
        <h1 className="font-head font-bold text-4xl sm:text-5xl tracking-tight">Delete your account</h1>

        {user === false ? (
          <div className="mt-8 border rounded-lg p-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
            <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>You need to be signed in to delete your account.</p>
            <Link to="/"><Button className="rounded-md mt-4" style={{ background: "var(--ek-primary)", color: "#fff" }} data-testid="delete-signin-btn">Sign in</Button></Link>
          </div>
        ) : (
          <>
            <div className="mt-6 border rounded-lg p-5 flex items-start gap-3" style={{ background: "rgba(240,97,109,0.06)", borderColor: "rgba(240,97,109,0.3)" }}>
              <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "var(--ek-error)" }} />
              <div className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>
                This permanently and immediately deletes <b style={{ color: "var(--ek-text)" }}>everything</b>: your encrypted evidence, incident timeline, generated reports, audit trail, and your account itself. <b style={{ color: "var(--ek-text)" }}>This cannot be undone.</b> Consider exporting a report first.
              </div>
            </div>

            <div className="mt-8 space-y-3 max-w-sm">
              <Label htmlFor="confirm">Type <b style={{ color: "var(--ek-text)" }}>DELETE</b> to confirm</Label>
              <Input id="confirm" data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-transparent" placeholder="DELETE" autoComplete="off" />
            </div>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  disabled={busy || confirm !== "DELETE"}
                  data-testid="delete-account-btn"
                  className="rounded-md mt-6"
                  style={{ background: "var(--ek-error)", color: "#fff", opacity: confirm === "DELETE" && !busy ? 1 : 0.5 }}
                >
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting…</> : <><Trash2 className="h-4 w-4 mr-2" /> Permanently delete my account</>}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-head tracking-tight">Delete everything — are you sure?</AlertDialogTitle>
                  <AlertDialogDescription style={{ color: "var(--ek-text-secondary)" }}>
                    This immediately and permanently erases your account and every record. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel-btn" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>Keep my account</AlertDialogCancel>
                  <AlertDialogAction onClick={doDelete} data-testid="delete-confirm-final-btn" className="rounded-md" style={{ background: "var(--ek-error)", color: "#fff" }}>
                    Yes, delete permanently
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </main>
    </div>
  );
}
